import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, CreditCard, LockKeyhole, Mail, ShieldCheck, UploadCloud } from 'lucide-react';
import Button from '../components/ui/Button';
import { auth } from '../services/firebase';
import {
  FALLBACK_PLANS,
  BillingTier,
  CheckoutPlanId,
  PublicPlanConfig,
  formatLimit,
  formatUsdPrice,
  getBillingSummary,
  getPlanById,
  getPlanForTier,
  getSubscriptionErrorMessage,
  initializeSubscription,
  type BillingSummary,
} from '../services/subscription';

const pricingTiers: BillingTier[] = ['free', 'starter', 'growth', 'business', 'enterprise'];

const PricingPage: React.FC = () => {
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'annual'>('monthly');
  const [billingSummary, setBillingSummary] = useState<BillingSummary | null>(null);
  const [loadError, setLoadError] = useState('');
  const [checkoutError, setCheckoutError] = useState('');
  const [checkoutPlanId, setCheckoutPlanId] = useState<CheckoutPlanId | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadBilling = async () => {
      setIsLoading(true);
      setLoadError('');

      try {
        const summary = await getBillingSummary();
        if (isMounted) setBillingSummary(summary);
      } catch (error) {
        if (isMounted) setLoadError(getSubscriptionErrorMessage(error));
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadBilling();

    return () => {
      isMounted = false;
    };
  }, []);

  const plans = billingSummary?.plans ?? FALLBACK_PLANS;
  const currentPlan = getPlanById(plans, billingSummary?.customer.effectivePlanId ?? 'free');
  const usage = billingSummary?.usage;

  const visiblePlans = useMemo(() => {
    return pricingTiers.map((tier) => getPlanForTier(plans, tier, billingInterval));
  }, [billingInterval, plans]);

  const handleCheckout = async (plan: PublicPlanConfig) => {
    if (!plan.checkoutEnabled || plan.id === 'free' || plan.id === 'enterprise') return;

    if (!auth.currentUser) {
      window.location.assign('/signin');
      return;
    }

    setCheckoutError('');
    setCheckoutPlanId(plan.id as CheckoutPlanId);

    try {
      const checkout = await initializeSubscription(plan.id as CheckoutPlanId);
      window.location.assign(checkout.authorizationUrl);
    } catch (error) {
      setCheckoutError(getSubscriptionErrorMessage(error));
      setCheckoutPlanId(null);
    }
  };

  return (
    <div className="page-stack">
      <header className="page-header">
        <div>
          <h1 style={{ fontSize: '2.2rem', marginBottom: '0.55rem' }}>Pricing</h1>
          <p className="page-kicker">Simple plans for uploading data, viewing analytics, and asking GabMic questions.</p>
        </div>
        <span className="soft-chip">
          <LockKeyhole size={16} />
          Secure Paystack redirect
        </span>
      </header>

      <div className="premium-card" style={{ padding: '1rem', display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <strong style={{ display: 'block' }}>
            {isLoading ? 'Checking your plan...' : `Current plan: ${currentPlan.name}`}
          </strong>
          <p style={{ margin: 0, fontSize: '0.9rem' }}>
            {usage
              ? `${formatRemaining(usage.remaining.uploads, 'upload')} and ${formatRemaining(usage.remaining.aiQuestions, 'AI question')} remaining this month.`
              : 'Sign in to see your monthly upload and AI-question allowance.'}
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'hidden' }}>
          <button type="button" style={segmentStyle(billingInterval === 'monthly')} onClick={() => setBillingInterval('monthly')}>
            Monthly
          </button>
          <button type="button" style={segmentStyle(billingInterval === 'annual')} onClick={() => setBillingInterval('annual')}>
            Yearly
          </button>
        </div>
      </div>

      {(loadError || checkoutError) && (
        <div className="premium-card" style={{ padding: '1rem', borderColor: 'var(--status-warning)', color: 'var(--text-main)' }}>
          {checkoutError || loadError}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem' }}>
        {visiblePlans.map((plan) => {
          const isCurrent = billingSummary?.customer.effectivePlanId === plan.id;
          const isEnterprise = plan.id === 'enterprise';
          const isFree = plan.id === 'free';

          return (
            <section
              key={plan.id}
              className="premium-card pricing-card"
              style={{
                padding: '1.25rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                borderColor: isCurrent ? 'rgba(255,122,0,0.62)' : undefined,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'flex-start' }}>
                <div>
                  <span className="mono-label" style={{ marginBottom: '0.45rem' }}>{plan.badge}</span>
                  <h2 style={{ fontSize: '1.45rem', marginBottom: '0.45rem' }}>{plan.name}</h2>
                  <strong style={{ color: 'var(--gabmic-orange)', fontSize: '1.45rem' }}>{formatUsdPrice(plan)}</strong>
                </div>
                {isCurrent && (
                  <span className="soft-chip" style={{ minHeight: '32px', padding: '0.35rem 0.65rem' }}>
                    <CheckCircle2 size={15} />
                    Current
                  </span>
                )}
              </div>

              <div style={{ display: 'grid', gap: '0.6rem' }}>
                <PlanFeature>{formatLimit(plan.usageLimits.uploads, 'uploads')} per month</PlanFeature>
                <PlanFeature>{formatLimit(plan.usageLimits.aiQuestions, 'Ask GabMic questions')} per month</PlanFeature>
                <PlanFeature>CSV and Excel analytics</PlanFeature>
                <PlanFeature>Server-enforced usage limits</PlanFeature>
              </div>

              <div style={{ marginTop: 'auto' }}>
                {isEnterprise ? (
                  <a href="mailto:sales@gabmic.ai?subject=GabMic%20Enterprise%20plan">
                    <Button variant="outline" fullWidth>
                      <Mail size={17} />
                      Contact us
                    </Button>
                  </a>
                ) : isFree ? (
                  <a href="/upload">
                    <Button variant={isCurrent ? 'outline' : 'secondary'} fullWidth>
                      <UploadCloud size={17} />
                      {isCurrent ? 'Current plan' : 'Start free'}
                    </Button>
                  </a>
                ) : (
                  <Button
                    variant={isCurrent ? 'outline' : 'primary'}
                    fullWidth
                    onClick={() => handleCheckout(plan)}
                    disabled={isCurrent || checkoutPlanId === plan.id}
                  >
                    <CreditCard size={17} />
                    {checkoutPlanId === plan.id ? 'Opening Paystack...' : isCurrent ? 'Current plan' : 'Upgrade securely'}
                  </Button>
                )}
              </div>
            </section>
          );
        })}
      </div>

      <div className="premium-card" style={{ padding: '1rem', display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
        <ShieldCheck size={18} color="var(--gabmic-orange)" style={{ marginTop: '0.2rem' }} />
        <p style={{ margin: 0, fontSize: '0.9rem' }}>
          GabMic never stores card details. Checkout opens on Paystack, then GabMic verifies the transaction and plan on the server before access changes.
        </p>
      </div>
    </div>
  );
};

const PlanFeature: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span style={{ display: 'flex', gap: '0.55rem', color: 'var(--text-secondary)', fontSize: '0.92rem' }}>
    <CheckCircle2 size={16} color="var(--gabmic-orange)" style={{ flex: '0 0 auto', marginTop: '0.12rem' }} />
    {children}
  </span>
);

const segmentStyle = (active: boolean): React.CSSProperties => ({
  minWidth: '96px',
  padding: '0.72rem 1rem',
  background: active ? 'rgba(255,122,0,0.16)' : 'transparent',
  border: 0,
  color: active ? 'var(--text-main)' : 'var(--text-secondary)',
  font: 'inherit',
  fontWeight: 700,
  cursor: 'pointer',
});

const formatRemaining = (value: number | null, noun: string) => {
  if (value === null) return `custom ${noun}s`;
  return `${value.toLocaleString()} ${noun}${value === 1 ? '' : 's'}`;
};

export default PricingPage;
